import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createLead } from '../../services/leadEngineService';
import { getEnquiryAttributionPayload, getEnquiryAttribution } from '../../stores/auraCore';
import { trackAuraClick } from '../../services/auraClient';
import { track } from '../../utils/track';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR + i);
const GUEST_OPTIONS = ['Under 30', '30–60', '60–120', '120–200', '200+'];
const BUDGET_OPTIONS = ['Under £20k', '£20k–50k', '£50k–100k', '£100k+', 'Flexible'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const S = {
  container: {
    background: '#f6f1e8',
    border: '1px solid rgba(168, 132, 38, 0.25)',
    borderRadius: '2px',
    padding: '36px',
    fontFamily: 'Inter, sans-serif',
    color: '#171717',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  heading: {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '24px',
    fontWeight: '400',
    lineHeight: '1.2',
    marginBottom: '4px',
    color: '#171717',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#7a7a7a',
    marginBottom: '28px',
    fontWeight: '400',
  },
  group: { marginBottom: '24px' },
  label: {
    display: 'block',
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '8px',
    color: '#171717',
  },
  input: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid #171717',
    padding: '10px 0',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    color: '#171717',
    outline: 'none',
    transition: 'border-color 180ms ease',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid #171717',
    padding: '10px 0',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    color: '#171717',
    outline: 'none',
    transition: 'border-color 180ms ease',
    resize: 'none',
    minHeight: '60px',
    boxSizing: 'border-box',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  checkboxInput: { cursor: 'pointer', accentColor: '#8f7420' },
  checkboxLabel: { fontSize: '14px', fontWeight: '400', cursor: 'pointer' },
  pillGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
  },
  budgetPillGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
  },
  pill: {
    padding: '10px 12px',
    border: '1px solid #8f7420',
    background: 'transparent',
    color: '#171717',
    fontFamily: 'Inter, sans-serif',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 180ms ease',
    borderRadius: '2px',
  },
  pillActive: {
    backgroundColor: '#8f7420',
    color: '#ffffff',
    borderColor: '#8f7420',
  },
  submit: {
    width: '100%',
    padding: '14px 24px',
    background: '#8f7420',
    color: '#ffffff',
    border: 'none',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    cursor: 'pointer',
    transition: 'opacity 180ms ease',
    marginTop: '28px',
    borderRadius: '2px',
  },
  submitDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  privacy: {
    fontSize: '11px',
    color: '#7a7a7a',
    marginTop: '16px',
    fontWeight: '400',
    lineHeight: '1.5',
  },
  error: {
    color: '#d32f2f',
    fontSize: '13px',
    marginBottom: '16px',
    marginTop: '-8px',
  },
  successWrap: { textAlign: 'center', padding: '48px 24px' },
  successIcon: { fontSize: '48px', marginBottom: '16px', color: '#8f7420' },
  successHeading: {
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '28px',
    fontWeight: '400',
    marginBottom: '12px',
    color: '#171717',
  },
  successText: { fontSize: '14px', color: '#7a7a7a', marginBottom: '24px' },
  resetBtn: {
    background: 'transparent',
    border: '1px solid #8f7420',
    color: '#8f7420',
    padding: '10px 20px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    borderRadius: '2px',
  },
  select: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid #171717',
    padding: '10px 0',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    color: '#171717',
    outline: 'none',
    transition: 'border-color 180ms ease',
    boxSizing: 'border-box',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%238f7420' d='M1 1l5 5 5-5'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0 center',
    paddingRight: '20px',
  },
  // ── Modal styles ──
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(0,0,0,0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    overflowY: 'auto',
  },
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.12)',
    color: '#171717',
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  eyebrow: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#8f7420',
    marginBottom: '8px',
  },
};

const focusGold = (e) => { e.target.style.borderBottomColor = '#8f7420'; };
const focusReset = (e) => { e.target.style.borderBottomColor = '#171717'; };

// ─── Component ───────────────────────────────────────────────────────────────

export default function VenueEnquiryForm({
  listingId = null,
  venueId = null,
  vendorId = null,
  vendorName = '',
  vendorEmail = null,
  venueName = '',
  responseTime = null,
  sticky = true,
  entityType = 'venue',   // 'venue' | 'vendor' | 'planner'
  modal = false,          // wrap in full-screen portal overlay
  onClose = null,         // required when modal=true
}) {
  const isVenue = entityType === 'venue';
  const ENTITY_LABEL_MAP = {
    venue:         'Venue',
    planner:       'Planner',
    boutique:      'Boutique',
    photographer:  'Photographer',
    videographer:  'Videographer',
    florist:       'Florist',
    caterer:       'Caterer',
    hair_makeup:   'Hair & Makeup',
    stationery:    'Stationery',
    jeweller:      'Jeweller',
    entertainment: 'Entertainment',
    transport:     'Transport',
    bridal_wear:   'Bridal',
    accessories:   'Accessories',
    guest_attire:  'Attire',
    health_beauty: 'Wellness',
  };
  const entityLabel = ENTITY_LABEL_MAP[entityType] || 'Vendor';
  const displayName = vendorName || venueName || '';
  const defaultMsg = isVenue
    ? `Hi${displayName ? ' ' + displayName : ''}, I'm writing to enquire about availability for our wedding at ${venueName || 'your venue'}. We would love to discuss our vision and understand what you can offer for our date.`
    : `Hi${displayName ? ' ' + displayName : ''}, I'm interested in your services for our wedding and would love to hear more about how you work, your availability, and investment levels.`;

  const [form, setForm] = useState({
    message: defaultMsg,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    eventMonth: '',
    eventYear: '',
    exactDate: false,
    guestCount: null,
    budgetRange: '',
    locationPreference: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notificationWarning, setNotificationWarning] = useState(false);
  const submitLockRef = useRef(false);
  const [error, setError] = useState(null);
  const formRef = useRef(null);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setError(null);
  };

  // ── Funnel: the couple began filling this form ─────────────────────────────
  // Inline form, so there is no "open" event — the honest signal is the first
  // keystroke. Deduped per listing per Aura session, so editing ten fields is one
  // started enquiry. Separates "nobody engaged the form" from "they engaged and
  // abandoned it"; without the distinction both look like zero enquiries.
  const startedRef = useRef(false);
  const noteEnquiryStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    const id = listingId || venueId || vendorId || null;
    const a = getEnquiryAttribution(id);
    if (!id || !a.queryId || !a.auraSessionId) return;
    if (markEnquiryStarted(id, a.auraSessionId)) {
      trackAuraClick(a.queryId, a.auraSessionId, id, a.rank, 'enquiry_started');
    }
  };

  const onChange = (e) => {
    noteEnquiryStarted();
    const { name, value, type, checked } = e.target;
    set(name, type === 'checkbox' ? checked : value);
  };

  const validate = () => {
    if (!form.firstName.trim()) return 'Please enter your first name';
    if (!form.email.trim()) return 'Please enter your email address';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email address';
    if (!form.eventMonth || !form.eventYear) return 'Please select your wedding month and year';
    if (!form.guestCount) return 'Please select an approximate guest count';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setLoading(true);
    try {
      const aura = getEnquiryAttributionPayload();
      const auraAttrib = getEnquiryAttribution(listingId || venueId || vendorId || null);
      // Build normalized lead payload
      const result = await createLead({
        leadSource: isVenue ? 'venue_page' : 'vendor_page',
        leadChannel: 'form',
        leadType: isVenue ? 'venue_enquiry' : 'vendor_enquiry',

        listingId: listingId || venueId || vendorId || null,
        listingType: entityType,
        venueId: venueId || listingId || null,
        vendorId: vendorId || listingId || null,

        auraSessionId: auraAttrib.auraSessionId ?? undefined,
        intentSummary: aura.intentSummary || undefined,

        firstName: form.firstName,
        lastName: form.lastName,
        fullName: [form.firstName, form.lastName].filter(Boolean).join(' '),
        email: form.email,
        phone: form.phone,

        weddingMonth: form.eventMonth,
        weddingYear: form.eventYear ? Number(form.eventYear) : undefined,
        exactDateKnown: form.exactDate,
        guestCount: form.guestCount,
        budgetRange: form.budgetRange || undefined,
        locationPreference: form.locationPreference || undefined,

        message: form.message,

        consentMarketing: false,
        consentDataProcessing: true,
      });

      if (!result.success) throw result.error || new Error('Failed to create lead');

      if (result.notificationFailed || result.routingFailed) {
        setNotificationWarning(true);
      }

      const resolvedListingId = listingId || venueId || vendorId || null;
      // Resolved against the cross-tab bridge, not just this tab's store: the usual
      // journey is "View Profile" in a NEW tab, where queryId is null and the old
      // guard silently dropped every conversion. See auraAttribution.ts.
      if (auraAttrib.queryId && auraAttrib.auraSessionId && resolvedListingId) {
        trackAuraClick(auraAttrib.queryId, auraAttrib.auraSessionId, resolvedListingId, auraAttrib.rank, 'enquire');
      }

      track('enquiry_submitted', {
        vendor_id: resolvedListingId,
        category:  entityType === 'venue' ? 'wedding-venues' : 'vendor',
      });

      setSubmitted(true);
    } catch (err) {
      console.error('VenueEnquiryForm: submit failed:', err);
      setError(err?.message || 'Failed to send enquiry. Please try again.');
    } finally {
      setLoading(false);
      submitLockRef.current = false;
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setForm({
      message: defaultMsg,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      eventMonth: '',
      eventYear: '',
      exactDate: false,
      guestCount: null,
      budgetRange: '',
      locationPreference: '',
    });
    setError(null);
  };

  // Lock body scroll when in modal mode
  useEffect(() => {
    if (!modal) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [modal]);

  // Escape key in modal mode
  useEffect(() => {
    if (!modal || !onClose) return;
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [modal, onClose]);

  const containerStyle = {
    ...S.container,
    ...(modal ? { maxWidth: 480, width: '100%', position: 'relative' } : {}),
    ...(!modal && sticky ? {
      position: 'sticky',
      top: '140px',
      alignSelf: 'start',
      zIndex: 10,
      maxHeight: 'calc(100vh - 160px)',
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(143,116,32,0.3) transparent',
    } : {}),
  };

  // ── Success state ──────────────────────────────────────────────────────────

  const successContent = (
    <div style={containerStyle}>
      {modal && onClose && (
        <button onClick={onClose} aria-label="Close" style={S.closeBtn}>✕</button>
      )}
      <div style={S.successWrap}>
        <div style={S.successIcon}>&#10003;</div>
        <h3 style={S.successHeading}>Enquiry Received</h3>
        <p style={S.successText}>
          {displayName || (isVenue ? 'The venue' : 'The team')} has your brief and will follow up within 24 hours. A confirmation has been sent to your email.
        </p>
        {notificationWarning && (
          <p style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 4, padding: '8px 12px', margin: '8px 0 0' }}>
            Your enquiry was received. There was a delay with the notification. Our team will follow up to confirm it has been delivered.
          </p>
        )}
        <button style={S.resetBtn} onClick={modal ? onClose : handleReset}>
          {modal ? 'Done' : 'Send Another Enquiry'}
        </button>
      </div>
    </div>
  );

  if (submitted && !modal) return successContent;
  if (submitted && modal) return createPortal(
    <div onClick={onClose} style={S.overlay}>{successContent}</div>,
    document.body
  );

  // ── Form ───────────────────────────────────────────────────────────────────

  const eyebrow = isVenue ? 'VENUE ENQUIRY' : `${entityLabel.toUpperCase()} ENQUIRY`;

  const formContent = (
    <div style={containerStyle} onClick={(e) => e.stopPropagation()}>
      {modal && onClose && (
        <button onClick={onClose} aria-label="Close" style={S.closeBtn}>✕</button>
      )}
      <div style={S.eyebrow}>{eyebrow}</div>
      <h2 style={S.heading}>{displayName || (isVenue ? 'Enquire About This Venue' : 'Send an Enquiry')}</h2>
      {responseTime
        ? <div style={S.subtitle}>Typically replies within {responseTime}</div>
        : <div style={S.subtitle}>Share your brief below and {displayName || 'the team'} will follow up directly.</div>
      }

      <form ref={formRef} onSubmit={handleSubmit}>
        {/* Honeypot, hidden from humans, bots fill it; checked server-side in submit-lead */}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ display: 'none' }} />
        {/* Message */}
        <div style={S.group}>
          <label style={S.label}>Your Message</label>
          <textarea name="message" value={form.message} onChange={onChange} style={S.textarea} onFocus={focusGold} onBlur={focusReset} />
        </div>

        {/* First + Last Name */}
        <div style={S.row}>
          <div style={S.group}>
            <label style={S.label}>First Name</label>
            <input type="text" name="firstName" value={form.firstName} onChange={onChange} style={S.input} onFocus={focusGold} onBlur={focusReset} />
          </div>
          <div style={S.group}>
            <label style={S.label}>Last Name</label>
            <input type="text" name="lastName" value={form.lastName} onChange={onChange} style={S.input} onFocus={focusGold} onBlur={focusReset} />
          </div>
        </div>

        {/* Email + Phone */}
        <div style={S.row}>
          <div style={S.group}>
            <label style={S.label}>Email</label>
            <input type="email" name="email" value={form.email} onChange={onChange} style={S.input} onFocus={focusGold} onBlur={focusReset} />
          </div>
          <div style={S.group}>
            <label style={S.label}>Phone <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#9a9a9a' }}>(optional)</span></label>
            <input type="tel" name="phone" value={form.phone} onChange={onChange} style={S.input} onFocus={focusGold} onBlur={focusReset} />
          </div>
        </div>

        {/* Event Date */}
        <div style={S.row}>
          <div style={S.group}>
            <label style={S.label}>Wedding Month</label>
            <select name="eventMonth" value={form.eventMonth} onChange={onChange} style={S.select} onFocus={focusGold} onBlur={focusReset}>
              <option value="">Select Month</option>
              {MONTHS.map((m, i) => <option key={i} value={m}>{m}</option>)}
            </select>
          </div>
          <div style={S.group}>
            <label style={S.label}>Wedding Year</label>
            <select name="eventYear" value={form.eventYear} onChange={onChange} style={S.select} onFocus={focusGold} onBlur={focusReset}>
              <option value="">Select Year</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* Exact Date Checkbox */}
        <div style={S.group}>
          <label style={S.checkbox}>
            <input type="checkbox" name="exactDate" checked={form.exactDate} onChange={onChange} style={S.checkboxInput} />
            <span style={S.checkboxLabel}>I have a confirmed date</span>
          </label>
        </div>

        {/* Guest Count */}
        <div style={S.group}>
          <label style={S.label}>Approximate Guest Count</label>
          <div style={S.pillGrid}>
            {GUEST_OPTIONS.map((label) => (
              <button
                key={label}
                type="button"
                style={{ ...S.pill, ...(form.guestCount === label ? S.pillActive : {}) }}
                onClick={() => set('guestCount', form.guestCount === label ? null : label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Budget Range */}
        <div style={S.group}>
          <label style={S.label}>Investment Range <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#9a9a9a' }}>(optional)</span></label>
          <div style={S.budgetPillGrid}>
            {BUDGET_OPTIONS.map((label) => (
              <button
                key={label}
                type="button"
                style={{ ...S.pill, ...(form.budgetRange === label ? S.pillActive : {}) }}
                onClick={() => set('budgetRange', form.budgetRange === label ? '' : label)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && <div style={S.error}>{error}</div>}

        {/* Submit */}
        <button type="submit" disabled={loading} style={{ ...S.submit, ...(loading ? S.submitDisabled : {}) }}>
          {loading ? 'Sending...' : 'Send Enquiry'}
        </button>

        {/* Privacy */}
        <div style={S.privacy}>
          Your details are shared only with {displayName || 'the venue'} and handled in line with our privacy policy.
        </div>
      </form>
    </div>
  );

  if (!modal) return formContent;

  return createPortal(
    <div onClick={onClose} style={S.overlay}>
      {formContent}
    </div>,
    document.body
  );
}
